import './tooltip.scss';

import { For } from '@chakra-ui/react';
import { TooltipTypeEnum } from '@components/map/tooltips/tooltip.types';
import { useAppSelector } from '@hooks/redux.hooks';
import { useMapTooltip } from '@hooks/useMapTooltip';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { selectActiveOrganization } from '@redux/authent/authent.selectors';
import { selectPopupsByMapId } from '@redux/maps/maps.selectors';
import { selectLocalMapFilterByKey } from '@redux/settings/settings.selectors';
import { selectTargets } from '@redux/situation/situation.selectors';
import { SHOW_PERMANENT_TOOLTIP_MIN_ZOOM } from '@utils/map/mapZoom.constants';
import { isOpenedPopupByType } from '@utils/popup.utils';
import { isAircraft, isDrone, isVCA } from '@utils/target.utils';

import { Target } from '@/types/c2/c2.types';
import { LayerNameEnum } from '@/types/map.types';
import { PopupTypeEnum } from '@/types/popup.types';
import { TargetLocation } from '@/types/sensor/sensor.types';

import { useMapContext } from '../MapContext';
import TargetTooltip from './TargetTooltip';

function PermanentTooltips() {
  const { viewState } = useMapContext();
  const isAircraftShown = useAppSelector((state) => selectLocalMapFilterByKey(state, 'displayTargetAircraft'));
  const isDroneShown = useAppSelector((state) => selectLocalMapFilterByKey(state, 'displayTargetDrone'));
  const isVcaShown = useAppSelector((state) => selectLocalMapFilterByKey(state, 'displayTargetVca'));
  const [position, featureToDisplay] = useMapTooltip([
    LayerNameEnum.TARGETS,
    LayerNameEnum.EVENTS,
    LayerNameEnum.SENSOR_LABELS,
    LayerNameEnum.NO_FLY_ZONE_FILLS,
  ]);
  const popups = useSelectorWithMapId(selectPopupsByMapId);
  const showMeasureTooltip = isOpenedPopupByType(popups, PopupTypeEnum.MEASURE_INFO);
  const organization = useAppSelector(selectActiveOrganization);

  const targets: Target<TargetLocation>[] = useSelectorWithReplayMode(selectTargets);
  if (showMeasureTooltip || !viewState.zoom || viewState.zoom <= SHOW_PERMANENT_TOOLTIP_MIN_ZOOM) {
    return null;
  }

  const permanentTargets = targets
    .filter(
      (target) =>
        (isAircraftShown && isAircraft(target, organization)) ||
        (isDroneShown && isDrone(target, organization)) ||
        (isVcaShown && isVCA(target, organization)),
    )
    .filter(
      (target) =>
        !position ||
        !featureToDisplay ||
        featureToDisplay.type !== TooltipTypeEnum.TARGET ||
        target.id !== featureToDisplay.data.id,
    );
  return <For each={permanentTargets}>{(target) => <TargetTooltip data={target} key={target.id} isPermanent />}</For>;
}

export default PermanentTooltips;
